import {useMediaQuery} from '@chakra-ui/react';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Col,
  UiBox,
  UiButton,
  UiGridItem,
  UiImage,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
} from '~/components/elements';
import {QUERY_MOBILE} from '~/constants';
import {HAIR_LOSS_TELECONSULTATION_KEYS} from '~/constants/medical-questionnaires';
import {useInput} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {Option} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const Questionnaire4Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const [isDesktop] = useMediaQuery(`(min-width: ${QUERY_MOBILE})`);
  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[HAIR_LOSS_TELECONSULTATION_KEYS.LOOKING_FOR_HELP]?.data,
  );
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const NONE_VALUE = 'NONE';
  const {wpAuthUser} = medicalQuestionnaire;
  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === HAIR_LOSS_TELECONSULTATION_KEYS.LOOKING_FOR_HELP);

  let medicalOptions: Option[] = [];
  if (questionnaire?.options?.length) {
    medicalOptions = questionnaire?.options?.filter(i => i?.value !== NONE_VALUE);
  }

  const defaultOptions = medicalOptions.reduce<Record<string, boolean>>((res, cur) => {
    res[cur.value] = false;

    if (currentAnswer?.selectedOptions?.length) {
      currentAnswer.selectedOptions.map((i: string | number) => {
        if (i !== NONE_VALUE) res[i] = true;
      });
    }

    return res;
  }, {});

  const resetOptions = medicalOptions.reduce<Record<string, boolean>>((res, cur) => {
    res[cur.value] = false;

    return res;
  }, {});

  const {inputs, setInputs, validation} = useInput({
    defaultOptions,
    noneOptions: (currentAnswer?.selectedOption?.length && currentAnswer?.selectedOption[0] === NONE_VALUE) || false,
  });
  const dataSubmit = Object.keys(inputs.defaultOptions).filter(i => inputs.defaultOptions[i] === true);

  const onClickOption = (value: string | number) => {
    if (value === NONE_VALUE) {
      setInputs({noneOptions: !inputs.noneOptions, defaultOptions: resetOptions});
    } else {
      setInputs({
        noneOptions: false,
        defaultOptions: {...inputs.defaultOptions, [value]: !inputs.defaultOptions[value]},
      });
    }
  };

  const onClickSubmit = async () => {
    dispatch(
      setQuestionnaireAnswer({
        key: HAIR_LOSS_TELECONSULTATION_KEYS.LOOKING_FOR_HELP,
        index: questionnaire?.index || '',
        data: {selectedOptions: inputs?.noneOptions ? [NONE_VALUE] : dataSubmit},
      }),
    );

    dispatch(setNextQuestionnaire({value: HAIR_LOSS_TELECONSULTATION_KEYS.SELECT_SYMPTOMS}));
  };

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      disableNext={!dataSubmit.length && !inputs.noneOptions}
      handleOnNext={onClickSubmit}
      checkoutStep={questionnaire?.checkoutStep}
    >
      <Col alignItems="center" textAlign="center">
        <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.medium} />
        <UiText fontSize="xl" fontWeight="bold" mt={spacing.large}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiText fontSize="sm" mt={spacing.small}>
          Select all that apply
        </UiText>
        <UiBox mt={spacing.small} w="100%">
          <UiSimpleGrid
            templateColumns="repeat(4,1fr)"
            gap={4}
            mt={spacing.smedium}
            px="5%"
            minChildWidth={spacing.big}
            width="100%"
          >
            {questionnaire?.options?.map((i, index: number) => {
              return (
                <UiGridItem
                  key={i.value}
                  colSpan={isDesktop ? 2 : 4}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <UiButton
                    key={`${i.label}-${i.value}`}
                    title={i.label}
                    variant={inputs.defaultOptions[i.value] ? 'solid' : 'toggle'}
                    onClick={() => onClickOption(i.value)}
                    minWidth="40%"
                    width={'100%'}
                    height={spacing.standard}
                    px={5}
                  />
                </UiGridItem>
              );
            })}
          </UiSimpleGrid>
        </UiBox>
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire4Screen;
